import React, { useEffect } from "react";
import classNames from "classnames";
import styles from "./ModalShell.module.scss";

interface ModalShellProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: number;
  /** override body padding/scroll (drill-down manages its own) */
  bodyClassName?: string;
}

/** Overlay + centered panel + header/close/footer used by every HO modal. */
export default function ModalShell({
  title,
  subtitle,
  onClose,
  children,
  footer,
  maxWidth = 560,
  bodyClassName,
}: ModalShellProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    // ho-no-print: never let a modal get captured into the exported PDF
    <div className={classNames(styles.overlay, "ho-no-print")} onClick={onClose}>
      <div
        className={styles.panel}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.headText}>
            <div className={styles.title}>{title}</div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={classNames(styles.body, bodyClassName)}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
}
